/**
 * Prezentacja stanu rekordu w banku zleceń (cykl życia z §15 rozdziału FTL).
 *
 * **Źródłem prawdy dla zestawu stanów jest backend**:
 * `apps/api/src/user-offers/planning-status.ts`. Tutaj są wyłącznie etykiety
 * i kolory — rzeczy, których backend nie potrzebuje.
 *
 * Dlaczego to druga kopia, a nie import: monorepo ma dziś tylko `apps/*`, bez
 * wspólnego pakietu, a CRA nie kompiluje modułów spoza `src`. Zgodności pilnuje
 * test (`stanPlanowania.test.ts`), który czyta plik backendu i porównuje
 * zestawy — dokładnie tak samo jak przy `statusZlecenia.ts`.
 *
 * ## Uwaga na trzy osobne osie
 *
 * Zlecenie odpowiada jednocześnie na trzy pytania i **nie wolno ich mieszać
 * w jednej kolumnie**:
 *
 * - **stan planowania** (ten plik) — jak daleko jest planowanie;
 * - **stan akceptacji** (`statusZlecenia.ts`) — czy jest zapisane w TMS klienta;
 * - **ryzyko** (`atRisk`) — czy coś zagraża realizacji.
 *
 * Zlecenie obsadzone i zagrożone jest jednocześnie „obsadzone" i „zagrożone".
 * Pokazanie samego alertu ukryłoby informację, czy auto stoi pod zlecenie.
 */

import { calendarDatePassed } from '../../shared/dateTime';

export const STATUS_PRE_ORDER = 'pre_order';
export const STATUS_NEEDS_DATA = 'needs_data';
export const STATUS_READY = 'ready';
export const STATUS_TENTATIVE = 'tentative';
export const STATUS_ASSIGNED = 'assigned';
export const STATUS_CARRIER_ASSIGNED = 'carrier_assigned';
export const STATUS_HANDED_TO_TRADE = 'handed_to_trade';
export const STATUS_SOLD_BY_TRADE = 'sold_by_trade';
export const STATUS_INACTIVE = 'inactive';
/**
 * Zlecenie zakończone w TMS (status Z) — stan końcowy za przełącznikiem
 * `tmsCompletedFinal` (decyzja użytkownika 19.10.2026, pkt 6).
 */
export const STATUS_COMPLETED = 'completed';

export const ALL_PLANNING_STATUSES = [
  STATUS_PRE_ORDER,
  STATUS_NEEDS_DATA,
  STATUS_READY,
  STATUS_TENTATIVE,
  STATUS_ASSIGNED,
  STATUS_CARRIER_ASSIGNED,
  STATUS_HANDED_TO_TRADE,
  STATUS_SOLD_BY_TRADE,
  STATUS_INACTIVE,
  STATUS_COMPLETED,
] as const;

export type PlanningStatus = (typeof ALL_PLANNING_STATUSES)[number];

type PlanningChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

export type PlanningStatusAppearance = {
  label: string;
  color: PlanningChipColor;
};

/**
 * Wygląd stanu. Etykiety wprost z §15, żeby nie tworzyć drugiego języka obok
 * tego, którym mówi dokument i klient.
 */
export const STATUS_APPEARANCE: Record<PlanningStatus, PlanningStatusAppearance> = {
  [STATUS_PRE_ORDER]: { label: 'Prezlecenie', color: 'secondary' },
  [STATUS_NEEDS_DATA]: { label: 'Wymaga uzupełnienia', color: 'warning' },
  [STATUS_READY]: { label: 'Gotowe do planowania', color: 'info' },
  [STATUS_TENTATIVE]: { label: 'Wstępnie planowane', color: 'primary' },
  [STATUS_ASSIGNED]: { label: 'Obsadzone flotą', color: 'success' },
  [STATUS_CARRIER_ASSIGNED]: { label: 'Obsadzone przewoźnikiem', color: 'success' },
  [STATUS_HANDED_TO_TRADE]: { label: 'Przekazane do Trade', color: 'info' },
  [STATUS_SOLD_BY_TRADE]: { label: 'Sprzedane przez Trade', color: 'success' },
  [STATUS_INACTIVE]: { label: 'Nieaktualne', color: 'default' },
  [STATUS_COMPLETED]: { label: 'Zrealizowane', color: 'success' },
};

/** Wygląd wartości spoza zestawu — nie zmyślamy stanu. */
export const UNKNOWN_STATUS_APPEARANCE: PlanningStatusAppearance = {
  label: 'Stan nieznany',
  color: 'default',
};

export function isKnownPlanningStatus(w: unknown): w is PlanningStatus {
  return typeof w === 'string' && (ALL_PLANNING_STATUSES as readonly string[]).includes(w);
}

/**
 * Stany za przełącznikiem funkcji (`OrderStatus.featureFlag` w API): w filtrze
 * pojawiają się tylko ze słownika, który oddaje je przy włączonym
 * przełączniku. Kopia z kodu (zanim słownik przyjdzie) ich nie proponuje —
 * bez przełącznika filtr ma wyglądać jak dotąd. Etykietę i kolor mają zawsze.
 */
export const FEATURE_GATED_STATUSES: readonly PlanningStatus[] = [STATUS_COMPLETED];

export const STATUS_CHOICES = ALL_PLANNING_STATUSES.filter((id) => !FEATURE_GATED_STATUSES.includes(id)).map(
  (id) => ({
    id,
    name: STATUS_APPEARANCE[id].label,
  }),
);

/** Wycinek słownika z API potrzebny do etykiet — patrz `stany.ts`. */
export type DictionaryStatus = { code: string; label: string };

/**
 * Wygląd stanu: **etykieta ze słownika, kolor z kodu**.
 *
 * Słownik (`useStatuses`) jest konfiguracją klienta — może zmienić etykietę
 * albo dodać stan, którego kod nie zna. Taki stan dostaje etykietę
 * z konfiguracji i kolor neutralny; stan znany kodowi dostaje swój kolor.
 * Bez słownika (zanim przyjdzie, albo gdy API nie odpowie) zostaje kopia
 * z kodu — ta sama, którą zasiała migracja. „Stan nieznany" jest dopiero
 * wtedy, gdy nie zna go ani słownik, ani kod.
 */
export function planningStatusAppearance(
  w: unknown,
  states: ReadonlyArray<DictionaryStatus> = [],
): PlanningStatusAppearance {
  const fromDictionary =
    typeof w === 'string' ? states.find((s) => s.code === w) : undefined;
  if (fromDictionary) {
    return {
      label: fromDictionary.label,
      color: isKnownPlanningStatus(w) ? STATUS_APPEARANCE[w].color : 'default',
    };
  }
  return isKnownPlanningStatus(w) ? STATUS_APPEARANCE[w] : UNKNOWN_STATUS_APPEARANCE;
}

/** Wybór do filtra: ze słownika, a zanim przyjdzie — kopia z kodu. */
export function planningStatusChoices(
  states: ReadonlyArray<DictionaryStatus> = [],
): { id: string; name: string }[] {
  return states.length > 0
    ? states.map((s) => ({ id: s.code, name: s.label }))
    : STATUS_CHOICES;
}

/** Wybór do filtra na liście. */

/**
 * Czy rekord przekroczył termin ważności.
 *
 * Sam fakt, bez skutku — **stan zmienia człowiek, nie zegar**. Widok pokazuje
 * to jako ostrzeżenie przy dacie. Uzasadnienie i pytanie do klienta: plik
 * backendu.
 */
export function isExpired(
  deadline: string | null | undefined,
  now: Date = new Date(),
): boolean {
  // Termin to data (północ UTC dnia) — mija z początkiem swojego dnia
  // w Polsce, jak w API, a nie o 02:00 i nie według strefy przeglądarki.
  return calendarDatePassed(deadline, now);
}
